import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { notificationPreferenceService } from '../../services/api'

const initialState = {
  preferences: {},
  loading: false,
  saving: false,
  error: null,
  dirty: false
}

export const fetchPreferences = createAsyncThunk(
  'notificationPreferences/fetchPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationPreferenceService.getAll()
      return response.data.obj
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Falha ao carregar preferencias'
      )
    }
  }
)

export const updatePreferences = createAsyncThunk(
  'notificationPreferences/updatePreferences',
  async (preferences, { rejectWithValue }) => {
    try {
      const response = await notificationPreferenceService.update(preferences)
      return response.data.obj
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Falha ao atualizar preferencias'
      )
    }
  }
)

export const updateCategory = createAsyncThunk(
  'notificationPreferences/updateCategory',
  async ({ category, channelEmail, channelPush, enabled }, { rejectWithValue }) => {
    try {
      const response = await notificationPreferenceService.updateCategory(
        category,
        channelEmail,
        channelPush,
        enabled
      )
      return response.data.obj
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Falha ao atualizar categoria'
      )
    }
  }
)

export const resetPreferences = createAsyncThunk(
  'notificationPreferences/resetPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationPreferenceService.reset()
      return response.data.obj
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Falha ao resetar preferencias'
      )
    }
  }
)

const notificationPreferencesSlice = createSlice({
  name: 'notificationPreferences',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setDirty: (state, action) => {
      state.dirty = action.payload
    },
    updateLocalPreference: (state, action) => {
      const { category, index, field, value } = action.payload
      if (state.preferences[category] && state.preferences[category][index]) {
        state.preferences[category][index][field] = value
        state.dirty = true
      }
    },
    toggleCategoryChannel: (state, action) => {
      const { category, channel, value } = action.payload
      if (state.preferences[category]) {
        state.preferences[category].forEach((item) => {
          item[channel] = value
          if (!item.channel_email && !item.channel_push) {
            item.enabled = false
          } else {
            item.enabled = true
          }
        })
        state.dirty = true
      }
    },
    toggleCategoryAll: (state, action) => {
      const { category, enabled } = action.payload
      if (state.preferences[category]) {
        state.preferences[category].forEach((item) => {
          item.channel_email = enabled
          item.channel_push = enabled
          item.enabled = enabled
        })
        state.dirty = true
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreferences.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.loading = false
        state.preferences = action.payload
        state.dirty = false
      })
      .addCase(fetchPreferences.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(updatePreferences.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(updatePreferences.fulfilled, (state, action) => {
        state.saving = false
        state.preferences = action.payload
        state.dirty = false
      })
      .addCase(updatePreferences.rejected, (state, action) => {
        state.saving = false
        state.error = action.payload
      })

      .addCase(updateCategory.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.saving = false
        state.preferences = action.payload
        state.dirty = false
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.saving = false
        state.error = action.payload
      })

      .addCase(resetPreferences.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(resetPreferences.fulfilled, (state, action) => {
        state.saving = false
        state.preferences = action.payload
        state.dirty = false
      })
      .addCase(resetPreferences.rejected, (state, action) => {
        state.saving = false
        state.error = action.payload
      })
  }
})

export const {
  clearError,
  setDirty,
  updateLocalPreference,
  toggleCategoryChannel,
  toggleCategoryAll
} = notificationPreferencesSlice.actions

export const selectPreferences = (state) => state.notificationPreferences.preferences
export const selectPreferencesLoading = (state) => state.notificationPreferences.loading
export const selectPreferencesSaving = (state) => state.notificationPreferences.saving
export const selectPreferencesError = (state) => state.notificationPreferences.error
export const selectPreferencesDirty = (state) => state.notificationPreferences.dirty

export default notificationPreferencesSlice.reducer
